import {
  type ComponentShape,
  GEOMETRY_TOLERANCE,
  type ShapedComponent,
  shapeHolesOf,
  shapeRingOf,
} from '@repo/api-contracts';
import type { Raster } from '@repo/warp-engine';

/** 픽셀당 한 변의 표본 수(4×4 = 16점). */
export const SHAPE_MASK_SUPERSAMPLE = 4;

/** 캐시에 보관하는 마스크 수(가장 오래된 것부터 밀어낸다). */
export const SHAPE_MASK_CACHE_SIZE = 8;

/** 구간 양끝을 넓히는 양(mm) — 표본 간격(≥ 1/(4·pxPerMm))보다 훨씬 작다. */
const SPAN_EPSILON_MM = 1e-9;

/** 2차 캐시 — 문자열 키(모양 직렬화 + 크기) 최근 8개. */
const cache = new Map<string, Uint8Array>();

/** 1차 캐시 — 같은 shape 객체면 JSON 직렬화 없이 마지막 결과를 바로 찾는다. */
const byShape = new WeakMap<
  ComponentShape,
  { readonly sizeKey: string; readonly mask: Uint8Array }
>();

/** 한 표본 행(y mm)에서 모양 안에 드는 x 구간들 [x0, x1, x0, x1, ...]. 경계는 포함. */
type RowSpans = (yMm: number, out: number[]) => void;

const ellipseSpans = (component: ShapedComponent): RowSpans => {
  const rx = component.widthMm / 2;
  const ry = component.heightMm / 2;
  return (yMm, out) => {
    if (rx <= 0 || ry <= 0) return;
    const t = (yMm - ry) / ry;
    // isPointInShape 의 타원식((dx/rx)²+(dy/ry)² ≤ 1+허용오차)과 같은 경계.
    const rest = 1 + GEOMETRY_TOLERANCE - t * t;
    if (rest < 0) return;
    const half = rx * Math.sqrt(rest);
    out.push(rx - half, rx + half);
  };
};

const polygonSpans = (component: ShapedComponent): RowSpans => {
  const rings = [shapeRingOf(component), ...shapeHolesOf(component)];
  const edges: number[] = [];
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i += 1) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      if (a === undefined || b === undefined || a.yMm === b.yMm) continue;
      edges.push(a.xMm, a.yMm, b.xMm, b.yMm);
    }
  }
  const crossings: number[] = [];
  return (yMm, out) => {
    crossings.length = 0;
    for (let e = 0; e < edges.length; e += 4) {
      const ax = edges[e] ?? 0;
      const ay = edges[e + 1] ?? 0;
      const bx = edges[e + 2] ?? 0;
      const by = edges[e + 3] ?? 0;
      // isPointInPolygon 과 같은 반개구간 규칙 — 꼭짓점을 두 번 세지 않는다.
      if (ay > yMm !== by > yMm) crossings.push(ax + ((yMm - ay) * (bx - ax)) / (by - ay));
    }
    crossings.sort((p, q) => p - q);
    // 바깥 고리와 구멍 변을 함께 모은 even-odd 짝 — 구멍 안은 짝 사이에서 빠진다.
    for (let k = 0; k + 1 < crossings.length; k += 2)
      out.push(crossings[k] ?? 0, crossings[k + 1] ?? 0);
  };
};

/**
 * 스캔라인 + 4×4 슈퍼샘플링. 픽셀당 4개 표본 행마다 모양 구간을 구하고, 그 안에 드는 표본 열을 픽셀별로 센다.
 * 복잡도 O(H·4·E + W·H·16) — 표본마다 모양 판정을 하지 않는다.
 */
const computeMask = (
  component: ShapedComponent,
  width: number,
  height: number,
  pxPerMm: number,
): Uint8Array => {
  const n = SHAPE_MASK_SUPERSAMPLE;
  const mask = new Uint8Array(width * height);
  const spansOf =
    component.shape?.type === 'ellipse' ? ellipseSpans(component) : polygonSpans(component);
  const columns = width * n;
  /** 표본 열 k 의 x(mm) — 전수 검사 때의 (col + (i+0.5)/n)/pxPerMm 와 같은 식. */
  const sampleX = (k: number): number => (Math.floor(k / n) + ((k % n) + 0.5) / n) / pxPerMm;
  const counts = new Uint16Array(width);
  const spans: number[] = [];

  for (let row = 0; row < height; row += 1) {
    counts.fill(0);
    for (let j = 0; j < n; j += 1) {
      spans.length = 0;
      spansOf((row + (j + 0.5) / n) / pxPerMm, spans);
      for (let s = 0; s + 1 < spans.length; s += 2) {
        // 경계 포함 — 교차 x 의 부동소수 반올림으로 변 위 표본이 빠지지 않게 아주 조금 넓힌다.
        const x0 = (spans[s] ?? 0) - SPAN_EPSILON_MM;
        const x1 = (spans[s + 1] ?? 0) + SPAN_EPSILON_MM;
        let first = Math.max(0, Math.ceil(x0 * pxPerMm * n - 0.5));
        while (first > 0 && sampleX(first - 1) >= x0) first -= 1;
        while (first < columns && sampleX(first) < x0) first += 1;
        let last = Math.min(columns - 1, Math.floor(x1 * pxPerMm * n - 0.5));
        while (last + 1 < columns && sampleX(last + 1) <= x1) last += 1;
        while (last >= 0 && sampleX(last) > x1) last -= 1;
        for (let k = first; k <= last; k += 1) {
          const col = (k / n) | 0;
          counts[col] = (counts[col] ?? 0) + 1;
        }
      }
    }
    const offset = row * width;
    for (let col = 0; col < width; col += 1) {
      mask[offset + col] = Math.round((255 * (counts[col] ?? 0)) / (n * n));
    }
  }
  return mask;
};

/**
 * 컴포넌트 래스터(width×height px)의 모양 커버리지 0~255. 사각형(shape 없음)은 null — 마스크 생략.
 * 결과는 (shape, width, height, pxPerMm)로만 정해지므로 최근 8개를 메모이즈한다(같은 shape 객체는 WeakMap 1차 캐시). 적중 시 같은 인스턴스를 돌려주니
 * 호출자는 읽기만 해야 한다(applyShapeMask 는 마스크를 바꾸지 않는다).
 */
export const rasterizeShapeMask = (
  component: ShapedComponent,
  width: number,
  height: number,
  pxPerMm: number,
): Uint8Array | null => {
  const { shape } = component;
  if (shape === undefined) return null;
  // 타원은 mm 크기에도 의존(ceil 한 px 가 같아도 mm 가 다르면 마스크가 다르다)하므로 mm 도 키에 넣는다.
  const sizeKey = `${component.widthMm}|${component.heightMm}|${width}|${height}|${pxPerMm}`;
  const last = byShape.get(shape);
  if (last !== undefined && last.sizeKey === sizeKey) return last.mask;
  const key = `${JSON.stringify(shape)}|${sizeKey}`;
  const hit = cache.get(key);
  if (hit !== undefined) {
    byShape.set(shape, { sizeKey, mask: hit });
    return hit;
  }
  const mask = computeMask(component, width, height, pxPerMm);
  cache.set(key, mask);
  byShape.set(shape, { sizeKey, mask });
  if (cache.size > SHAPE_MASK_CACHE_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  return mask;
};

/** straight RGBA 래스터의 알파에 마스크를 곱한다(제자리). */
export const applyShapeMask = (raster: Raster, mask: Uint8Array): void => {
  const { data } = raster;
  for (let i = 0; i < mask.length; i += 1) {
    const a = i * 4 + 3;
    data[a] = Math.round(((data[a] ?? 0) * (mask[i] ?? 0)) / 255);
  }
};
